// TRACES (EUDR Information System) compatibility, as computed by the backend
// (graniot/common/traces_parcel.py). A local check against the published
// GeoJSON spec, never an acceptance decision by the EU system.

export const TRACES_COLORS = {
  rejected: '#d32f2f',
}

// What reaches TRACES is the traceability export: by default a Point under 4 ha and
// the normalised polygon otherwise, or every parcel as a polygon when the client asks
// for geometry_type='polygon'. Both files are judged; the original upload and the raw
// stored geometry are not sent as such (older verdicts may still carry those layers).
const LAYERS = [
  // ['source', 'Original upload'],
  // ['stored', 'Stored geometry'],
  ['export', 'TRACES export'],
  ['export_polygon', 'TRACES export (polygons only)'],
]

// Plain-language copy per backend code. Keys are the English source strings (i18n).
export const TRACES_ISSUE_COPY = {
  'TR-POLY-HOLES': ['Polygon has holes', 'TRACES does not accept interior rings. Split the parcel into separate polygons.'],
  'TR-POLY-SELF-INTERSECTION': ['Boundary crosses itself', 'Redraw the boundary so its edges do not cross.'],
  'TR-POLY-STRAIGHT-LINE': ['Polygon has no area', 'Its vertices lie on a line. Redraw or remove the parcel.'],
  'TR-POLY-NOT-CLOSED': ['Polygon is not closed', 'The first and last vertex must be the same point.'],
  'TR-POLY-TOO-FEW-POINTS': ['Too few vertices', 'A polygon needs at least 4 points.'],
  'TR-POLY-INVALID': ['Invalid polygon', 'Redraw the boundary.'],
  'TR-GEOM-INVALID-AFTER-TRUNCATION': ['Breaks at 6 decimals', 'TRACES cuts coordinates to 6 decimals and this boundary becomes invalid. Simplify it.'],
  'TR-GEOM-TYPE-UNSUPPORTED': ['Unsupported geometry type', 'TRACES accepts only points and polygons.'],
  'TR-GEOM-MISSING': ['Missing geometry', 'Draw or re-upload the parcel.'],
  'TR-GEOM-EMPTY': ['Empty geometry', 'Draw or re-upload the parcel.'],
  'TR-GEOM-MALFORMED': ['Unreadable geometry', 'Re-upload the parcel from the original file.'],
  'TR-GEOM-COLLECTION': ['Geometry collection', 'Send polygons or points instead.'],
  'TR-COORD-OUT-OF-RANGE': ['Coordinates out of range', 'Check that the file uses WGS84 longitude and latitude.'],
  'TR-COORD-NOT-NUMBER': ['Coordinates are not numbers', 'Coordinates must be numeric values.'],
  'TR-COORD-3D': ['Includes elevation', 'Only longitude and latitude are used.'],
  'TR-COORD-LIKELY-SWAPPED': ['Longitude and latitude may be swapped', 'GeoJSON order is longitude, latitude.'],
  'TR-COORD-CROSSES-ANTIMERIDIAN': ['Crosses the 180° meridian', 'Split the parcel at the antimeridian.'],
  'TR-POINT-AREA-TOO-SMALL': ['Area below the TRACES minimum', 'Points need an area of at least 0.0001 ha (1 m²).'],
  'TR-POINT-AREA-OVER-4HA': ['Point used for a plot over 4 ha', 'Plots over 4 ha must be sent as a polygon.'],
  'TR-POINT-AREA-DEFAULTED': ['Area missing', 'TRACES will assume 4 ha.'],
  'TR-PROP-AREA-NOT-NUMBER': ['Area is not a number', 'Area must be a number in hectares.'],
  'TR-PROP-COUNTRY-INVALID': ['Invalid country code', 'Use a two-letter ISO 3166 code.'],
  'TR-PROP-COUNTRY-MISSING': ['Country missing', 'Add a two-letter ProducerCountry code.'],
}
const FALLBACK_TITLE = 'Geometry issue'

export const TRACES_HEADINGS = {
  rejected: 'TRACES compatibility issue',
}
const identity = (text) => text

// 'rejected' | null. Only an export TRACES would reject (either mode) is shown: a
// missing or unavailable check is unknown, never a failure, and draws nothing.
export const tracesStatus = (computed) => {
  const report = computed?.traces
  if (!report || report.status === 'unavailable') return null
  return LAYERS.some(([key]) => report[key]?.valid === false) ? 'rejected' : null
}

export const hasTracesWarning = (computed) => tracesStatus(computed) !== null

// One entry per code, errors first, listing every geometry version it affects.
export const tracesIssues = (computed) => {
  const report = computed?.traces
  if (!report) return []
  const byCode = new Map()
  LAYERS.forEach(([key, label]) => {
    const layer = report[key]
    if (!layer) return
    const issues = (layer.issues || []).filter(issue => issue.severity === 'error')
    const known = new Set(issues.map(issue => issue.code))
    // Older verdicts may carry codes without issue details.
    ;(layer.error_codes || []).filter(code => !known.has(code))
      .forEach(code => issues.push({ code, severity: 'error', message: '' }))
    issues.forEach(({ code, severity, message }) => {
      const entry = byCode.get(code) || { code, severity, messages: [], layers: [] }
      if (severity === 'error') entry.severity = 'error'
      if (message && !entry.messages.includes(message)) entry.messages.push(message)
      if (!entry.layers.includes(label)) entry.layers.push(label)
      byCode.set(code, entry)
    })
  })
  return [...byCode.values()].sort((a, b) => (a.severity === 'error' ? 0 : 1) - (b.severity === 'error' ? 0 : 1))
}

export const tracesIssueCopy = (code, t = identity) => {
  const [title, hint] = TRACES_ISSUE_COPY[code] || [FALLBACK_TITLE, '']
  return { title: t(title), hint: hint ? t(hint) : '' }
}

// Plain text, for aria-labels and anywhere HTML is not available.
export const tracesWarningText = (computed, t = identity) => {
  const status = tracesStatus(computed)
  if (!status) return ''
  const lines = [t(TRACES_HEADINGS[status])]
  tracesIssues(computed).forEach(issue => {
    const { title, hint } = tracesIssueCopy(issue.code, t)
    lines.push(`${title}${hint ? `: ${hint}` : ''}`)
  })
  return lines.join('\n')
}

export const escapeTracesHtml = (text) => String(text).replace(/[&<>"']/g, char =>
  ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]))

// Leaflet tooltips take an HTML string; every dynamic value is escaped.
export const tracesTooltipHtml = (computed, t = identity) => {
  const status = tracesStatus(computed)
  if (!status) return ''
  const e = escapeTracesHtml
  const color = TRACES_COLORS[status]
  const items = tracesIssues(computed).map(issue => {
    const { title, hint } = tracesIssueCopy(issue.code, t)
    return `<div style="margin-top:6px">`
      + `<div style="font-weight:600">${e(title)}</div>`
      + (hint ? `<div>${e(hint)}</div>` : '')
      + `</div>`
  }).join('')
  return `<div style="margin-top:8px;padding:6px 8px;border-left:3px solid ${color};background:rgba(0,0,0,0.03);`
    + `white-space:normal;min-width:240px;max-width:320px;line-height:1.35">`
    + `<div style="color:${color};font-weight:700">${e(t(TRACES_HEADINGS[status]))}</div>`
    + items
    + `</div>`
}
